import { AccountStatusEnum, SnackBarVariants, TransactionTypeEnum } from '@app/app.enum';
import withSnackBar from '@app/components/core/message.component';
import CustomButton from '@app/components/shared/custom-button.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import * as models from '@app/models';
import Injector from '@app/services';
import { AdminAccountService } from '@app/services/admin-account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { CircularProgress, Grid, InputAdornment, TextField, Typography } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Axios from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import React from 'react';
import { injectIntl } from 'react-intl';
import * as Yup from 'yup';

const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);
const adminService: AdminAccountService = Injector.get(AdminAccountService);

class AdjustBalanceComponent extends React.Component<models.IAdjustBalanceProps, models.IAdjustBalanceState> {
	private AdjustBalanceFactory: models.IAdjustBalanceFields;
	private AdjustBalanceSchema: any = Yup.object().shape<models.IAdjustBalanceFields>({
		amount: Yup.number()
			.required(intlService.GetFormattedMessage('app.billing.purchase-postage-amount-required-validation'))
			.min(1, intlService.GetFormattedMessage('app.billing.purchase-postage-amount-min-length-validation') + 1),
	});

	constructor(props: models.IAdjustBalanceProps) {
		super(props);
		this.state = {
			adjustloading: false,
			dataLoading: false,
			openDialog: false,
			transactionType: TransactionTypeEnum.Debit,
			amount: null,
			currentBalance: 0,
			visibleAccountNumber: null,
		};
		this.AdjustBalanceFactory = {
			amount: 0,
		};
	}

	public render() {
		const canAdjustBalanceFormRender = !this.props.isPartnerAccount;
		return (
			<React.Fragment>
				<Grid container={true} direction='row' alignContent='center' spacing={4}>
					<Grid
						container={true}
						item={true}
						direction='row'
						spacing={2}
						alignItems='center'
						justifyContent='center'
						md={!canAdjustBalanceFormRender ? 12 : 6}
						xs={12}
					>
						{this.renderCurrentBalanceWithIcon()}
					</Grid>
					{canAdjustBalanceFormRender && (
						<Grid item={true} container={true} md={3} xs={12} justifyContent='center'>
							{this.renderForm()}
						</Grid>
					)}
				</Grid>
			</React.Fragment>
		);
	}

	private renderCurrentBalanceWithIcon(): React.ReactNode {
		return (
			<React.Fragment>
				<Grid item={true}>
					<Icon name='quote' fontSize='extra-large' />
				</Grid>
				<Grid item={true}>
					<Grid container={true} direction='column'>
						<Grid item={true}>
							<Typography color='inherit' variant='caption'>
								{intlService.GetFormattedMessage('app.billing.purchase-current-balance-label')}
							</Typography>
						</Grid>
						<Grid item={true}>
							<Typography color='inherit' variant='h5'>
								{this.state.dataLoading ? (
									<span className='pl-1 circular-progress-position'>
										<CircularProgress size={14} />
									</span>
								) : (
									<span className='primary-color' id='currentBalance'>
										$ {this.props.balance}
									</span>
								)}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	private renderForm(): React.ReactNode {
		return (
			<React.Fragment>
				<Formik
					initialValues={this.AdjustBalanceFactory}
					enableReinitialize={true}
					onSubmit={(e) => this.setState({ openDialog: true })}
					validationSchema={this.AdjustBalanceSchema}
					render={(props: FormikProps<FormikValues>) => this.renderAdjustBalanceForm(props)}
				/>
				{this.renderCreditDialogBox()}
			</React.Fragment>
		);
	}

	private renderAdjustBalanceForm(props: FormikProps<FormikValues>): React.ReactNode {
		return (
			<React.Fragment>
				<Form noValidate={true} className='width-100'>
					<Grid container={true} direction='column' spacing={1}>
						<Grid item={true}>{this.renderDebitAndCreditButtons()}</Grid>
						<Grid item={true}>{this.renderBalanceFormField(props)}</Grid>
						<Grid item={true}>
							<CustomButton
								disabled={
									this.state.adjustloading ||
									!!Object.keys(props.errors).length ||
									this.props.account.status === AccountStatusEnum.Closed
								}
								buttonText={intlService.GetFormattedMessage(
									'app.account-details.account-balance-adjustment.adjust-button-text'
								)}
								loading={this.state.adjustloading}
							/>
						</Grid>
					</Grid>
				</Form>
			</React.Fragment>
		);
	}

	private renderDebitAndCreditButtons(): React.ReactNode {
		return (
			<ToggleButtonGroup
				value={this.state.transactionType}
				exclusive={true}
				onChange={(e: any, newValue: any) => {
					if (newValue !== null) {
						this.setState({ transactionType: newValue });
					}
				}}
				size='small'
			>
				<ToggleButton value={TransactionTypeEnum.Debit}>
					{this.props.intl.formatMessage({
						id: 'app.balance-history.transaction-type-label-Debit',
					})}
				</ToggleButton>
				<ToggleButton value={TransactionTypeEnum.Credit}>
					{this.props.intl.formatMessage({
						id: 'app.balance-history.transaction-type-label-Credit',
					})}
				</ToggleButton>
			</ToggleButtonGroup>
		);
	}

	private renderBalanceFormField(props: FormikProps<FormikValues>): React.ReactNode {
		return (
			<Field
				name='amount'
				label={intlService.GetFormattedMessage('app.billing.purchase-amount-label')}
				margin='normal'
				variant='outlined'
				component={TextField}
				type='number'
				disabled={this.props.account.status === AccountStatusEnum.Closed}
				value={props.values.amount}
				fullWidth={true}
				required={true}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					{
						props.handleChange('amount')(e);
						this.setState({
							amount: e.target.value,
						});
					}
				}}
				error={!!props.errors.amount}
				helperText={props.errors.amount}
				InputProps={{
					startAdornment: <InputAdornment position='start'>$</InputAdornment>,
				}}
			/>
		);
	}

	private renderCreditDialogBox() {
		const creditDialogTitleDescription: string = this.getCreditDialogTitleDescription();
		return (
			<CustomDialogBox
				open={this.state.openDialog}
				dialogTitle={intlService.GetFormattedMessage(
					'app.account-details.account-balance-adjustment.credit-dialog-label'
				)}
				dialogDescription={creditDialogTitleDescription}
				closeCustomDialog={(dialogPannel: string, isCancel: boolean) => {
					if (!isCancel) {
						this.adjustBalance({
							amount: this.state.amount,
							accountId: this.props.account.id,
							transactionType: this.state.transactionType,
						});
					}
					this.setState({
						openDialog: false,
					});
				}}
			/>
		);
	}

	private getCreditDialogTitleDescription(): string {
		return this.props.intl.formatHTMLMessage(
			{
				id:
					this.state.transactionType === TransactionTypeEnum.Debit
						? 'app.account-details.account-balance-adjustment.debit-dialog-description'
						: 'app.account-details.account-balance-adjustment.credit-dialog-description',
			},
			{
				0: this.props.account.name,
				1: this.state.amount,
			}
		);
	}

	private adjustBalance = async (data: models.IAdjustBalanceRequest) => {
		try {
			this.setState({
				adjustloading: true,
			});
			const result = await adminService.AdjustBalance(data);
			this.setState({
				adjustloading: false,
			});
			this.props.updateAccountBalance(result.balance);
			this.openSnackbar(
				intlService.GetFormattedMessage('app.account-details.account-balance-adjustment.snackbar-success-message')
			);
		} catch (error) {
			this.setState({
				adjustloading: false,
			});
			if (!Axios.isCancel(error)) {
				this.openSnackbar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	};

	private openSnackbar = (message: string | string[], variantName: SnackBarVariants = SnackBarVariants.Success) => {
		this.setState({
			adjustloading: false,
		});

		this.props.openSnackBar(message, variantName);
	};
}
export default withSnackBar(injectIntl(AdjustBalanceComponent));



// WEBPACK FOOTER //
// ./src/components/admin/customer/admin-account-adjust-balance.component.tsx